"use client";

import { motion, useReducedMotion } from "framer-motion";

const checks = [
  {
    title: "Brand & Entity Signals",
    body: "Consistent brand facts AI models can trust and connect.",
    icon: "M12 12a4 4 0 1 0 0-8a4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0",
    color: "bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300",
  },
  {
    title: "Schema & Structured Data",
    body: "Organization, FAQ, Service and Product markup.",
    icon: "M8 7l-5 5 5 5M16 7l5 5-5 5",
    color: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
  },
  {
    title: "Answer-First Content",
    body: "Clear, quotable answers to the questions buyers ask.",
    icon: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
    color: "bg-violet-50 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300",
  },
  {
    title: "Topical Authority",
    body: "In-depth content clusters that prove your expertise.",
    icon: "M12 2l9 5-9 5-9-5zM3 12l9 5 9-5M3 17l9 5 9-5",
    color: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  },
  {
    title: "AI Crawler Access",
    body: "robots.txt, sitemaps and llms.txt set up for AI crawlers.",
    icon: "M5 8h14v10H5zM12 4v4M9 13h.01M15 13h.01M2 12h3M19 12h3",
    color: "bg-pink-50 text-pink-700 dark:bg-pink-950/50 dark:text-pink-300",
  },
  {
    title: "E-E-A-T & Trust",
    body: "Author bios, reviews and credentials that build trust.",
    icon: "M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z",
    color: "bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
  },
  {
    title: "Mentions & Citations",
    body: "Earn mentions on the sites AI engines rely on.",
    icon: "M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1",
    color: "bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300",
  },
  {
    title: "Technical SEO Health",
    body: "Fast, crawlable, indexable pages for every engine.",
    icon: "M12 14l4-4M3.5 18a9 9 0 1 1 17 0",
    color: "bg-sky-50 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300",
  },
];

export default function AiSearchWhatWeOptimise() {
  const reduceMotion = useReducedMotion();

  return (
    <motion.section
      initial={reduceMotion ? false : { opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: reduceMotion ? 0 : 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="bg-white px-4 py-12 transition-colors sm:px-6 sm:py-16 lg:px-8 dark:bg-gray-950"
    >
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8">
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-3 text-center">
          <p className="font-poppins text-sm font-bold uppercase tracking-widest text-pink-700 dark:text-pink-400">
            What We Optimise
          </p>
          <h2 className="font-poppins text-3xl font-bold leading-tight text-indigo-950 sm:text-4xl dark:text-white">
            Everything AI Engines Look For{" "}
            <span className="text-violet-700 dark:text-violet-400">Before</span>{" "}
            They Cite You
          </h2>
          <p className="max-w-2xl text-base leading-relaxed text-slate-600 dark:text-slate-300">
            We combine technical SEO, structured data and answer-ready content
            so AI engines can find, understand and trust your brand.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {checks.map((check) => (
            <article
              key={check.title}
              className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-transform hover:-translate-y-1 dark:border-slate-800 dark:bg-slate-900"
            >
              <span
                className={`grid size-12 shrink-0 place-items-center rounded-xl ${check.color}`}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  className="size-5"
                >
                  <path d={check.icon} />
                </svg>
              </span>
              <div className="flex min-w-0 flex-col gap-2">
                <h3 className="font-poppins text-base font-bold text-indigo-950 dark:text-white">
                  {check.title}
                </h3>
                <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                  {check.body}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </motion.section>
  );
}
